import { createFileRoute } from '@tanstack/react-router';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import DraftSetup from '../-components/draft/DraftSetup';
import UserMeshingView from '../-components/phase-views/UserMeshingView';
import ReviewView from '../-components/phase-views/ReviewView';
import RunningView from '../-components/phase-views/RunningView';
import { ResultsPane } from '../-components/phase-views/ResultsPane';
import StepPlaceholder from '../-components/StepPlaceholder';
import PreprocessingLoader from '../-components/PreprocessingLoader';
import { useStudyContext } from '../-components/study-context';
import { AnalyzeControlBar } from '../-components/AnalyzeControlBar';
import { AnalyzeBanners } from '../-components/evaluate/AnalyzeBanners';
import { useAnalyzeShellClasses } from '../-components/grid/DashboardGrid';

// Bare URL renders the phase view matching the study's current status
// (no redirect). Step-specific URLs are owned by `setup.tsx` / `mesh.tsx` etc.
// and follow the same status-matching logic with placeholders when off-phase.
function StudyIndexRoute() {
  const { t } = useTranslation();
  const ctx = useStudyContext();
  // See `results.tsx` — the outer column's padding is part of the density
  // setting, not a fixed frame around it.
  const shell = useAnalyzeShellClasses();
  const { study, designPoints, geometries } = ctx;
  switch (study.status) {
    case 'draft':
      // Drafts land on Simulate — input picking lives there (no Setup step).
      return (
        <div className="h-full overflow-hidden">
          <DraftSetup
            study={study}
            geometries={geometries}
            section="simulate"
          />
        </div>
      );
    case 'awaiting_user_meshing':
      return (
        <div className="h-full overflow-y-auto bg-muted/30 p-6">
          <UserMeshingView
            study={study}
            designPoints={designPoints}
            geometries={geometries}
          />
        </div>
      );
    case 'awaiting_bc_review':
    case 'awaiting_mobo_review':
      return (
        <div className="h-full overflow-y-auto bg-muted/30 p-6">
          <ReviewView
            study={study}
            designPoints={designPoints}
            geometries={geometries}
            selectedDpId={ctx.selectedDpId}
            detailTab={ctx.detailTab}
            onSelectDp={ctx.onSelectDp}
          />
        </div>
      );
    case 'running':
      return (
        <div className="h-full overflow-y-auto bg-muted/30 p-6">
          <RunningView
            study={study}
            designPoints={designPoints}
            geometries={geometries}
            selectedDpId={ctx.selectedDpId}
            detailTab={ctx.detailTab}
            onSelectDp={ctx.onSelectDp}
          />
        </div>
      );
    case 'preprocessing':
      return <PreprocessingLoader />;
    case 'completed':
    case 'completed_with_issues':
      // ENG-1917 — the bare URL mounts the same results surface as
      // `/results`, so it gets the same control strip. Without it a reader
      // forwarded here from a share link has the workbench and no way to
      // leave Overview.
      //
      // `ResultsPane` with no `mode`: the three views are routes now, so this
      // URL is on none of them and the fallback in `resolveAnalyzeMode`
      // decides. Re-spelling `CompletedView`'s six props here instead is how
      // the two copies of this shell drift — see that component's note.
      return (
        <div className="flex h-full min-w-0 flex-col">
          <AnalyzeControlBar study={study} />
          <div
            className={cn(
              'flex min-h-0 flex-1 flex-col overflow-y-auto bg-muted/30',
              shell.outer,
            )}
          >
            {/* Same reason the control strip is here: this URL mounts the
                results surface without the `results.tsx` layout around it, so
                the banners it hosts have to be repeated. There is no tab
                switching on this route, so nothing depends on the mount
                surviving one — only on the banners existing at all. */}
            <AnalyzeBanners />
            {/* `min-h-0 flex-1` for the same reason as in `results.tsx`: the
                pane below declares `h-full`, and as a plain sibling of the
                banners that would overflow the column by exactly their
                height. */}
            <div className="min-h-0 min-w-0 flex-1">
              <ResultsPane />
            </div>
          </div>
        </div>
      );
    case 'failed':
    case 'cancelled':
      // Terminal-but-inspectable: the progress board surfaces the failure
      // summary (decoded errors) and the DP inspector lets the user drill into
      // residuals per design point. Pinned to the Simulate step in the header.
      return (
        <div className="h-full overflow-y-auto bg-muted/30 p-6">
          <RunningView
            study={study}
            designPoints={designPoints}
            geometries={geometries}
            selectedDpId={ctx.selectedDpId}
            detailTab={ctx.detailTab}
            onSelectDp={ctx.onSelectDp}
          />
        </div>
      );
    default: {
      // Exhaustive guard — surfaces a missing case if StudyStatus gains a value.
      const _exhaustive: never = study.status;
      return (
        <StepPlaceholder
          title={t('simopsRoutes.status.unknownTitle')}
          message={t('simopsRoutes.status.unknownMessage', {
            state: String(_exhaustive),
          })}
        />
      );
    }
  }
}

export const Route = createFileRoute('/_app/simops/$studyId/')({
  component: StudyIndexRoute,
});
